import { useSyncExternalStore } from "react";

/**
 * The connect panel's address, the light part the app layout reads (the panel itself is a lazy chunk):
 *
 * - `?connect=<what>` on any page opens the panel over it: `pick` (every source), `pick.<group>` (one group first,
 *   "databases"), a card (`postgres`, `code`, `sheets`…) or `developer` (send a link to the person with the keys);
 * - `?setup=open` opens the setup guide in the same panel; with `connect` as well, the card was opened from the guide
 *   and its way back leads there;
 * - `?fix=<source>` connects a source again (its check failed) instead of adding a new one.
 *
 * The three are kept from page to page like the record card's `?record=` (`routes/_app.tsx`), so a reload, a link
 * or a trip to Google, an app's sign-in or GitHub lands where the person left off.
 */

export type ConnectSearch = { connect?: string; setup?: "open"; fix?: string };

export const CONNECT_KEYS = ["connect", "setup", "fix"] as const;

/** `postgres`, `pick`, `pick.databases`, `developer`: checked against the cards by the panel itself. */
const TARGET = /^[a-z][a-z0-9_]{1,23}(\.[a-z]{2,16})?$/;
const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validateConnectSearch(search: Record<string, unknown>): ConnectSearch {
  const out: ConnectSearch = {};
  if (typeof search.connect === "string" && TARGET.test(search.connect)) out.connect = search.connect;
  // `open`, and `1` from an address typed by hand or an older link
  if (search.setup === "open" || search.setup === "1" || search.setup === 1 || search.setup === true)
    out.setup = "open";
  if (typeof search.fix === "string" && ID.test(search.fix)) out.fix = search.fix;
  return out;
}

/** The kinds Welcome offers as tiles, and where each opens the panel. */
export type GuideConnectKind = "database" | "shop" | "stripe" | "accounting" | "sheets" | "files" | "code";
const TARGET_OF: Record<GuideConnectKind, string> = {
  database: "pick.databases",
  shop: "pick.shops",
  accounting: "pick.money",
  stripe: "stripe",
  sheets: "sheets",
  files: "files",
  code: "code",
};
export const targetFor = (kind: GuideConnectKind): string => TARGET_OF[kind];

/** The address of the panel at a target on Home (Home's links, the checklist). */
export const homeConnectHref = (target: string): string => `/?connect=${target}`;

// ── the way back from a trip ───────────────────────────────────────────────────────────────────────────

/**
 * What a trip out brought back in the address: Google (`google=connected&grant=…`), an app's own sign-in
 * (`oauth=connected&add=<kind>&grant=…`, Trello's `oauth=token&ticket=…#token=…`) or GitHub (`github=<outcome>`).
 * A trip in a popup (`lib/trip-popup.ts`) brings the same answer by message instead (`takeTrip`); Trello's is then
 * finished in the popup and comes as `oauth=started&run=…&source=…`.
 */
export type ReturnTrip = {
  /** The card the trip was for. */
  card: string | null;
  google: string | null;
  oauth: string | null;
  grant: string | null;
  ticket: string | null;
  /** Trello's token, from the address's fragment. */
  token: string | null;
  github: string | null;
  /** GitHub: the signed list of installations to choose from (`#pick=`). Never kept past this page. */
  pick: string | null;
  /** Trello, finished in the popup: the run that checks it and its source. */
  run: string | null;
  source: string | null;
  at: number;
};

const TRIP_KEYS = ["google", "oauth", "grant", "ticket", "github", "run", "source"] as const;
const STORE = "adea:connect-trip";
const KEEP_MS = 30 * 60_000;
let trip: ReturnTrip | null = null;

/**
 * Before the router reads the address: a trip's answer is taken out of it (so it is used once, and never travels on
 * to another page), and an old Data address (`/data?view=connect&add=stripe`) becomes the panel's. The answer is
 * kept for this tab, so a reload while choosing (a Jira site, a BigQuery project) still has it.
 */
export function keepConnectReturn(): void {
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }
  const p = url.searchParams;
  const legacy = url.pathname.startsWith("/data") && p.get("view") === "connect";
  if (legacy) {
    p.delete("view");
    p.set("connect", p.get("add") || "pick");
  }
  const card = p.get("connect") ?? null;
  const fromTrip = !!card && TRIP_KEYS.some((k) => p.has(k));
  if (fromTrip) {
    const token = new URLSearchParams(url.hash.slice(1)).get("token");
    trip = {
      card,
      google: p.get("google"),
      oauth: p.get("oauth"),
      grant: p.get("grant"),
      ticket: p.get("ticket"),
      token,
      github: p.get("github"),
      // in the #fragment, so no server or proxy logs it
      pick: new URLSearchParams(url.hash.slice(1)).get("pick"),
      run: p.get("run"),
      source: p.get("source"),
      at: Date.now(),
    };
    for (const k of TRIP_KEYS) p.delete(k);
    p.delete("add");
    if (token || trip.pick) url.hash = "";
    try {
      // the token is never kept: it is spent at once
      window.sessionStorage.setItem(
        STORE,
        JSON.stringify({ ...trip, token: null, github: null, pick: null }),
      );
    } catch {
      // a private window: a reload starts the trip again
    }
  } else if (card) {
    try {
      const kept = JSON.parse(window.sessionStorage.getItem(STORE) ?? "null") as ReturnTrip | null;
      if (kept && kept.card === card && Date.now() - kept.at < KEEP_MS) trip = kept;
    } catch {
      // nothing kept
    }
  }
  if (legacy || fromTrip) {
    if (legacy) p.delete("add");
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  }
}

/** The trip that brought the person back, if it was for this card. */
export function tripFor(card: string): ReturnTrip | null {
  return trip && trip.card === card ? trip : null;
}

// the panel reads the trip as it draws; a trip that comes home by message draws it again
let version = 0;
const watchers = new Set<() => void>();
const changed = () => {
  version++;
  for (const fn of [...watchers]) fn();
};
const watch = (fn: () => void) => {
  watchers.add(fn);
  return () => {
    watchers.delete(fn);
  };
};
/** Draws again when a trip comes home or is spent. */
export const useTripVersion = (): number =>
  useSyncExternalStore(
    watch,
    () => version,
    () => version,
  );

/** A trip in a popup came home: its answer is taken as if the tab had come back with it in the address. */
export function takeTrip(answer: {
  card: string;
  google?: string;
  oauth?: string;
  grant?: string;
  github?: string;
  pick?: string;
  run?: string;
  source?: string;
}): void {
  trip = {
    card: answer.card,
    google: answer.google ?? null,
    oauth: answer.oauth ?? null,
    grant: answer.grant ?? null,
    ticket: null,
    token: null,
    github: answer.github ?? null,
    pick: answer.pick ?? null,
    run: answer.run ?? null,
    source: answer.source ?? null,
    at: Date.now(),
  };
  try {
    window.sessionStorage.setItem(STORE, JSON.stringify({ ...trip, github: null, pick: null }));
  } catch {
    // a private window: a reload starts the trip again
  }
  changed();
}

/** The trip's answer was used (connected, or the person started over): it is not offered again. */
export function spendTrip(): void {
  const had = trip !== null;
  trip = null;
  try {
    window.sessionStorage.removeItem(STORE);
  } catch {
    // nothing kept
  }
  if (had) changed();
}

/**
 * Where a trip out brings the person back: this page, as it is, with the panel open at the card (and the guide
 * behind it when it was open).
 */
export function returnPath(card: string): string {
  const url = new URL(window.location.href);
  const p = url.searchParams;
  for (const k of [...TRIP_KEYS, "add", "connect", "fix", "trip"]) p.delete(k);
  p.set("connect", card);
  const q = p.toString();
  return `${url.pathname}?${q}`;
}
